/**
 * The sound a new order makes.
 *
 * WHY IT IS SYNTHESISED RATHER THAN A FILE
 * ----------------------------------------
 * A kitchen screen runs on the shop's own machine, and the one thing this whole
 * system is built around is that the shop keeps working when the internet does
 * not. An audio file is one more thing to fetch, one more thing to have failed
 * to cache, and one more thing that is silently missing on the night it matters.
 * Two oscillators are none of those.
 *
 * WHY IT HAS TO BE UNLOCKED
 * -------------------------
 * A browser will not make a sound until somebody has interacted with the page.
 * That is a reasonable rule that would, left alone, produce exactly the failure
 * this is meant to prevent: a screen that has been showing tickets silently
 * since it was switched on, and a kitchen that stopped trusting it. So the
 * first click, key or touch anywhere on the page wakes the audio, and the
 * screen says so until it happens.
 */

let context = null;
let unlocked = false;
const listeners = new Set();

function tellListeners() {
  for (const listener of listeners) listener(unlocked);
}

/** Whether a sound would actually be heard right now. */
export function canBeHeard() {
  return unlocked;
}

/** Watch for the audio being woken, so a screen can stop asking. */
export function whenUnlocked(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Start listening for the interaction that lets sound play.
 *
 * Returns a function that stops listening. Safe to call more than once.
 */
export function unlockOnFirstGesture() {
  if (unlocked || typeof window === 'undefined') return () => {};

  const wake = () => {
    try {
      context = context ?? new (window.AudioContext ?? window.webkitAudioContext)();
      // Created suspended when there has been no gesture; resume is the point.
      context.resume?.();
      unlocked = context.state === 'running';
    } catch {
      // No audio on this machine. The screen still flashes.
      unlocked = false;
    }
    if (unlocked) {
      stop();
      tellListeners();
    }
  };

  const events = ['pointerdown', 'keydown', 'touchstart'];
  const stop = () => events.forEach((e) => window.removeEventListener(e, wake));
  events.forEach((e) => window.addEventListener(e, wake, { passive: true }));

  return stop;
}

/**
 * Two notes, the second above the first.
 *
 * Rising rather than falling, and short. A kitchen is a loud room and this has
 * to carry over an extractor fan without being the sort of noise somebody
 * eventually turns off at the wall.
 */
export function playNewOrderChime() {
  if (!context || context.state !== 'running') return false;

  const now = context.currentTime;
  for (const [at, hz] of [
    [0, 880],
    [0.14, 1320],
  ]) {
    const tone = context.createOscillator();
    const level = context.createGain();

    tone.type = 'sine';
    tone.frequency.value = hz;

    // Shaped rather than switched: a square edge on a sine is a click.
    level.gain.setValueAtTime(0.0001, now + at);
    level.gain.exponentialRampToValueAtTime(0.35, now + at + 0.02);
    level.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.25);

    tone.connect(level).connect(context.destination);
    tone.start(now + at);
    tone.stop(now + at + 0.3);
  }

  return true;
}

/** For tests, and for a screen that is being taken down. */
export function resetChime() {
  context?.close?.();
  context = null;
  unlocked = false;
  listeners.clear();
}
